import * as React from "react";
import { Check, ChevronDown } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "cn";
import { EASE_OUT } from "lib/ease";

import "./select.css";

const SelectContext = React.createContext(null);

const INSTANT_TRANSITION = { duration: 0 };
const CHEVRON_TRANSITION = { type: "spring", duration: 0.4, bounce: 0.3 };
const LIST_VARIANTS = {
  hidden: {},
  show: { transition: { staggerChildren: 0.035, delayChildren: 0.05 } },
};
const ITEM_VARIANTS = {
  hidden: { opacity: 0, y: -6, filter: "blur(3px)" },
  show: { opacity: 1, y: 0, filter: "blur(0px)" },
};

function useSelectContext(component) {
  const context = React.useContext(SelectContext);
  if (!context) throw new Error(`${component} must be used within <Select>`);
  return context;
}

function Select({
  children,
  className,
  defaultOpen = false,
  defaultValue,
  disabled = false,
  onOpenChange,
  onValueChange,
  open: openProp,
  value,
}) {
  const reduce = useReducedMotion() ?? false;
  const baseId = React.useId();
  const rootRef = React.useRef(null);
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const [labels, setLabels] = React.useState(() => new Map());
  const [placement, setPlacement] = React.useState("bottom");
  const controlledValue = value !== undefined;
  const currentValue = controlledValue ? value : internalValue;
  const controlledOpen = openProp !== undefined;
  const open = controlledOpen ? openProp : internalOpen;

  const setOpen = React.useCallback((nextOpen) => {
    if (!controlledOpen) setInternalOpen(nextOpen);
    onOpenChange?.(nextOpen);
  }, [controlledOpen, onOpenChange]);

  const select = React.useCallback((nextValue) => {
    if (!controlledValue) setInternalValue(nextValue);
    onValueChange?.(nextValue);
    setOpen(false);
  }, [controlledValue, onValueChange, setOpen]);

  const register = React.useCallback((nextValue, label) => {
    setLabels((current) => (current.get(nextValue) === label ? current : new Map(current).set(nextValue, label)));
  }, []);

  const unregister = React.useCallback((nextValue) => {
    setLabels((current) => {
      if (!current.has(nextValue)) return current;
      const next = new Map(current);
      next.delete(nextValue);
      return next;
    });
  }, []);

  React.useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("pointerdown", handlePointerDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [open, setOpen]);

  const context = React.useMemo(() => ({
    currentValue,
    disabled,
    labelFor: (nextValue) => nextValue === undefined ? undefined : labels.get(nextValue),
    listId: `${baseId}-list`,
    open,
    placement,
    reduce,
    register,
    select,
    setOpen,
    setPlacement,
    triggerId: `${baseId}-trigger`,
    unregister,
  }), [baseId, currentValue, disabled, labels, open, placement, reduce, register, select, setOpen, unregister]);

  return (
    <SelectContext.Provider value={context}>
      <div ref={rootRef} className={cn("ui-select", className)}>
        {children}
      </div>
    </SelectContext.Provider>
  );
}

function SelectTrigger({ children, className, ...props }) {
  const context = useSelectContext("SelectTrigger");
  const isTop = context.placement === "top";
  const cornerFrames = context.open ? [0, 0, 12] : [12, 0, 12];
  const cornerTransition = context.reduce
    ? INSTANT_TRANSITION
    : context.open
      ? { duration: 0.6, times: [0, 0.4, 1], ease: EASE_OUT }
      : { duration: 0.42, times: [0, 0.5, 1], ease: EASE_OUT };

  return (
    <motion.button
      aria-controls={context.listId}
      aria-expanded={context.open}
      aria-haspopup="listbox"
      disabled={context.disabled}
      id={context.triggerId}
      initial={false}
      onClick={() => context.setOpen(!context.open)}
      type="button"
      animate={{
        borderBottomLeftRadius: isTop ? 12 : cornerFrames,
        borderBottomRightRadius: isTop ? 12 : cornerFrames,
        borderTopLeftRadius: isTop ? cornerFrames : 12,
        borderTopRightRadius: isTop ? cornerFrames : 12,
      }}
      transition={{
        borderBottomLeftRadius: isTop ? INSTANT_TRANSITION : cornerTransition,
        borderBottomRightRadius: isTop ? INSTANT_TRANSITION : cornerTransition,
        borderTopLeftRadius: isTop ? cornerTransition : INSTANT_TRANSITION,
        borderTopRightRadius: isTop ? cornerTransition : INSTANT_TRANSITION,
      }}
      className={cn("ui-select-trigger", className)}
      {...props}
    >
      {children}
      <motion.span
        aria-hidden="true"
        animate={{ rotate: context.open ? 180 : 0 }}
        className="ui-select-chevron"
        transition={context.reduce ? { duration: 0 } : CHEVRON_TRANSITION}
      >
        <ChevronDown size={16} />
      </motion.span>
    </motion.button>
  );
}

function SelectValue({ className, placeholder, ...props }) {
  const context = useSelectContext("SelectValue");
  const label = context.labelFor(context.currentValue);

  return (
    <span className={cn("ui-select-value", label ? "is-filled" : "is-placeholder", className)} {...props}>
      {label ?? placeholder ?? context.currentValue ?? "Select"}
    </span>
  );
}

function SelectContent({ children, className }) {
  const context = useSelectContext("SelectContent");
  const { open, setPlacement, triggerId } = context;
  const innerRef = React.useRef(null);
  const [height, setHeight] = React.useState(0);
  const isTop = context.placement === "top";
  const nearGap = open ? 4 : 0;
  const nearRadius = open ? 12 : 0;

  React.useLayoutEffect(() => {
    const node = innerRef.current;
    if (!node) return undefined;

    const measure = () => setHeight(node.offsetHeight);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [children]);

  React.useLayoutEffect(() => {
    if (!open) return;
    const trigger = document.getElementById(triggerId);
    const node = innerRef.current;
    if (!trigger || !node) return;

    const rect = trigger.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom;
    const above = rect.top;
    setPlacementSafely(setPlacement, below < node.offsetHeight + 16 && above > below ? "top" : "bottom");
  }, [open, setPlacement, triggerId]);

  const gapTransition = context.open
    ? { type: "spring", duration: 0.6, bounce: 0.5, delay: 0.12 }
    : { type: "spring", duration: 0.3, bounce: 0.1 };
  const radiusTransition = context.open
    ? { duration: 0.3, ease: EASE_OUT, delay: 0.14 }
    : { duration: 0.16, ease: EASE_OUT };

  return (
    <motion.div
      aria-hidden={!context.open}
      aria-labelledby={context.triggerId}
      className={cn(
        "ui-select-content",
        isTop ? "ui-select-content--top" : "ui-select-content--bottom",
        className
      )}
      id={context.listId}
      inert={!context.open ? "" : undefined}
      initial={false}
      role="listbox"
      style={{
        overflow: "hidden",
        pointerEvents: context.open ? "auto" : "none",
        transformOrigin: isTop ? "bottom" : "top",
      }}
      animate={context.reduce ? {
        height: context.open ? height : 0,
        opacity: context.open ? 1 : 0,
      } : {
        borderBottomLeftRadius: isTop ? nearRadius : 12,
        borderBottomRightRadius: isTop ? nearRadius : 12,
        borderTopLeftRadius: isTop ? 12 : nearRadius,
        borderTopRightRadius: isTop ? 12 : nearRadius,
        height: context.open ? height : 0,
        marginBottom: isTop ? nearGap : 0,
        marginTop: isTop ? 0 : nearGap,
        opacity: context.open ? 1 : 0,
      }}
      transition={context.reduce ? { duration: 0.12 } : {
        borderBottomLeftRadius: isTop ? INSTANT_TRANSITION : radiusTransition,
        borderBottomRightRadius: isTop ? INSTANT_TRANSITION : radiusTransition,
        borderTopLeftRadius: isTop ? radiusTransition : INSTANT_TRANSITION,
        borderTopRightRadius: isTop ? radiusTransition : INSTANT_TRANSITION,
        height: context.open
          ? { type: "spring", duration: 0.42, bounce: 0.14 }
          : { duration: 0.26, ease: EASE_OUT, delay: 0.14 },
        marginBottom: isTop ? gapTransition : INSTANT_TRANSITION,
        marginTop: isTop ? INSTANT_TRANSITION : gapTransition,
        opacity: context.open ? { duration: 0.18 } : { duration: 0.16, delay: 0.12 },
      }}
    >
      <motion.div
        ref={innerRef}
        animate={context.open ? "show" : "hidden"}
        className="ui-select-content-inner"
        initial={false}
        variants={context.reduce ? undefined : LIST_VARIANTS}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

function setPlacementSafely(setPlacement, placement) {
  setPlacement((current) => current === placement ? current : placement);
}

function SelectItem({ children, className, disabled = false, value }) {
  const context = useSelectContext("SelectItem");
  const { register, unregister } = context;
  const selected = context.currentValue === value;
  const label = typeof children === "string" ? children : value;

  React.useLayoutEffect(() => {
    register(value, label);
    return () => unregister(value);
  }, [register, unregister, label, value]);

  return (
    <motion.li className="ui-select-item-row" variants={context.reduce ? undefined : ITEM_VARIANTS}>
      <button
        aria-selected={selected}
        className={cn("ui-select-item", selected && "is-selected", className)}
        disabled={disabled}
        onClick={() => context.select(value)}
        role="option"
        type="button"
      >
        <span>{children}</span>
        {selected ? <Check aria-hidden="true" size={14} /> : null}
      </button>
    </motion.li>
  );
}

export {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
};
